import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, apiErr, qrImage } from "@/lib/api";
import { PaymentDialog } from "@/components/PaymentDialog";
import { LOGO, IMG } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function GoldCard() {
  const [kids, setKids] = useState([]);
  const [childId, setChildId] = useState("");
  const [data, setData] = useState(null);
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("term");
  const [settings, setSettings] = useState(null);
  const [promo, setPromo] = useState("");
  const [ebValid, setEbValid] = useState(false);
  const [ebReason, setEbReason] = useState("");

  const checkPromo = async () => {
    const code = promo.trim();
    if (!code) { setEbValid(false); setEbReason(""); return; }
    try {
      const { data } = await api.get(`/early-bird/check?code=${encodeURIComponent(code)}`);
      setEbValid(!!data.valid);
      setEbReason(data.valid ? "" : (data.reason || ""));
    } catch { setEbValid(false); setEbReason(""); }
  };

  useEffect(() => { api.get("/children").then((r) => { setKids(r.data); if (r.data[0]) setChildId(r.data[0].id); }); api.get("/public/settings").then((r) => setSettings(r.data)); }, []);
  const load = () => childId && api.get(`/goldcard/${childId}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [childId]);

  const join = async (selectedPlan) => {
    setBusy(true);
    try { const { data } = await api.post(`/goldcard/${childId}/join`, { plan: selectedPlan, early_bird_code: promo.trim() || null }); setOrder(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const m = data?.membership;
  const child = data?.child;
  const reg = settings?.pricing || {};
  const founding = settings?.founding_active;
  const termPrice = (founding || ebValid) ? (reg.founding_membership ?? reg.membership) : reg.membership;

  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><Crown className="w-6 h-6 text-gold" /> Gold Explorer Membership</h1>
      <p className="text-slate-500">Your explorer's collectible Adventure Rock Gold Card.</p>

      <div className="mt-4 max-w-xs">
        <Select value={childId} onValueChange={setChildId}>
          <SelectTrigger className="rounded-xl" data-testid="gold-child-select"><SelectValue placeholder="Select explorer" /></SelectTrigger>
          <SelectContent>{kids.map((k) => <SelectItem key={k.id} value={k.id}>{k.preferred_name || k.first_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {child && (
        <div className="grid lg:grid-cols-2 gap-8 mt-6 items-start">
          <motion.div whileHover={{ rotateY: 6, rotateX: -3, scale: 1.02 }} style={{ perspective: 1200 }}
            className="relative rounded-3xl overflow-hidden border-2 border-gold/50 aspect-[1.586/1] shadow-2xl" data-testid="gold-card-display">
            <img src={IMG.goldCardBg} className="absolute inset-0 w-full h-full object-cover" alt="" />
            <div className="absolute inset-0 gold-shine" />
            <div className="relative p-6 h-full flex flex-col justify-between text-white">
              <div className="flex items-center justify-between">
                <img src={LOGO} className="w-14 h-14 object-contain" alt="logo" />
                <div className="text-right">
                  <p className="font-display font-bold text-gold tracking-[0.2em] text-xs">ADVENTURE ROCK</p>
                  <p className="font-display font-black text-gold tracking-widest text-sm">GOLD EXPLORER</p>
                </div>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="font-display font-black text-2xl">{child.first_name?.toUpperCase()}</p>
                  <p className="font-mono text-gold/80 text-sm">{child.explorer_id}</p>
                  <p className="text-[10px] text-white/50 mt-1">{m?.tier || "FOUNDING GOLD EXPLORER — TERM 1 2026"}</p>
                </div>
                {m?.status === "active" && (
                  <div className="bg-white p-1.5 rounded-lg" data-testid="gold-card-qr-code">
                    <img src={qrImage(m.qr_token, 90)} className="w-20 h-20" alt="QR" />
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          <div>
            {m?.status === "active" ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6" data-testid="gold-active">
                <p className="font-display font-bold text-emerald-700 text-lg">Active Membership ✨</p>
                <p className="text-slate-600 text-sm mt-1">Renews {m.expires_at}. Use the QR code for cashless snacks & quick check-in. Your physical card uses this same code.</p>
                <p className="text-xs text-slate-400 mt-3">Explorer ID: <b className="font-mono">{child.explorer_id}</b></p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="gold-join-panel">
                <p className="font-display font-bold text-navy text-lg">Unlock the Gold Card</p>
                <ul className="text-sm text-slate-600 mt-3 space-y-1.5">
                  <li>• Collectible digital + physical Gold Card with QR</li>
                  <li>• Priority booking when dates fill up</li>
                  <li>• Special Gold member pricing on every pass</li>
                  <li>• Birthday surprise & exclusive member activities</li>
                  <li>• Adventure Wallet for snacks & drinks</li>
                </ul>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <button onClick={() => setPlan("term")} data-testid="plan-term" className={`rounded-xl border-2 p-3 text-left transition-all ${plan === "term" ? "border-gold bg-gold/10" : "border-slate-200"}`}>
                    <p className="font-bold text-navy text-sm">Term 1</p>
                    <p className="text-turquoise font-bold">${termPrice}{(founding || ebValid) && reg.membership !== termPrice && <span className="text-slate-400 line-through ml-1 text-xs">${reg.membership}</span>}</p>
                  </button>
                  <button onClick={() => setPlan("school_year")} data-testid="plan-school" className={`rounded-xl border-2 p-3 text-left transition-all ${plan === "school_year" ? "border-gold bg-gold/10" : "border-slate-200"}`}>
                    <p className="font-bold text-navy text-sm">School Year</p>
                    <p className="text-turquoise font-bold">${reg.membership_school_year}</p>
                    <p className="text-[10px] text-slate-400">Best for regular families</p>
                  </button>
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-slate-500">Promo / Early Bird code</Label>
                  <Input value={promo} onChange={(e) => { setPromo(e.target.value); setEbValid(false); setEbReason(""); }} onBlur={checkPromo} className="rounded-xl mt-1" data-testid="gold-promo-input" placeholder="Enter your code" />
                  {ebValid && <p className="text-[11px] text-emerald-600 mt-1 font-semibold" data-testid="gold-promo-ok">✅ Early Bird price applied to Term 1!</p>}
                  {ebReason && <p className="text-[11px] text-coral mt-1" data-testid="gold-promo-err">{ebReason}</p>}
                </div>
                <Button onClick={() => join(plan)} disabled={busy} className="w-full mt-4 bg-gradient-to-r from-gold to-coral text-navy font-bold rounded-full" data-testid="join-gold-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Become a Gold Explorer"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <PaymentDialog order={order} open={!!order} onClose={() => setOrder(null)} onDone={load} />
    </div>
  );
}
